import { basicInfoAtom, cartAtom, deliveryServiceAtom, merchantStatusAtom, takeoutServiceAtom } from "@/atoms"
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config"
import { recordStorefrontEvent } from "@/domain/storefront-analytics"
import { Motorcycle, Pin, Plus } from "@/icons"
import { formatAddressLines } from "@/lib/format-address"
import { getNextOpeningMessage } from "@/lib/get-next-opening"
import { isServiceOpen } from "@/lib/is-service-open"
import { useAtom, useAtomValue } from "jotai"
import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { applyDeliveryQuotes, evaluateAddresses } from "../lib/evaluate-addresses"
import { useDeliveryQuotes } from "@/hooks/use-delivery-quotes"
import type { DeliveryQuoteReason } from "@/lib/delivery-quote/fetch-delivery-quote"
import { getStoredCompany } from "@/lib/company-storage"
import { checkoutAtom, isScheduleStepVisible, stepGuards } from "../state"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { findChannel } from "@/lib/scheduling/storefront-state"
import schedulingContextAtom from "@/atoms/scheduling-context"
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock"
import {
  ButtonLink,
  Callout,
  Card,
  Icon,
  PageContainer,
  Price,
  SectionHeader,
  SelectableCard,
  SelectableCardGroup,
  Text,
} from "@/components/ui"

export function ServiceTypeStep() {
  const { addresses } = useCheckoutContext()
  const cart = useAtomValue(cartAtom)
  const basicInfo = useAtomValue(basicInfoAtom)
  const merchantStatus = useAtomValue(merchantStatusAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)
  const takeoutService = useAtomValue(takeoutServiceAtom)
  const [state, setState] = useAtom(checkoutAtom)
  const schedulingContext = useAtomValue(schedulingContextAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const now = useAtomValue(minuteNowAtom)
  const scheduling = useStorefrontScheduling()
  const navigate = useNavigate()

  useEnforceWizardEntry("service", addresses)

  useEffect(() => {
    recordStorefrontEvent({ type: "checkout_step", checkoutStep: "address" })
  }, [])

  const isScheduling = state.orderTiming === "SCHEDULED"
  const takeoutChannel = findChannel(schedulingContext, "TAKEOUT")
  const deliveryChannel = findChannel(schedulingContext, "DELIVERY")
  const schedulableTakeout = scheduling.schedulableChannels.some((c) => c.serviceType === "TAKEOUT")
  const schedulableDelivery = scheduling.schedulableChannels.some(
    (c) => c.serviceType === "DELIVERY",
  )

  const storeToggledOff = merchantStatus === "UNAVAILABLE"
  const takeoutProvisioned = takeoutService != null && (!isScheduling || schedulableTakeout)
  const deliveryProvisioned = deliveryService != null && (!isScheduling || schedulableDelivery)
  const takeoutOpen = isScheduling
    ? schedulableTakeout
    : !storeToggledOff && isServiceOpen(takeoutService, now, timeZone)
  const deliveryOpen = isScheduling
    ? schedulableDelivery
    : !storeToggledOff && isServiceOpen(deliveryService, now, timeZone)
  const takeoutClosedHint = useMemo(() => {
    if (isScheduling) {
      return schedulableTakeout ? null : "Retirada não aceita agendamento no momento."
    }
    if (!takeoutProvisioned) return "Retirada não disponível neste estabelecimento."
    if (storeToggledOff) return "A loja está fechada no momento."
    if (takeoutOpen) return null
    const next = takeoutService?.serviceHours?.weekHours
      ? getNextOpeningMessage(takeoutService.serviceHours.weekHours)
      : null
    return next ? `Retirada fechada. ${next}` : "Retirada indisponível no momento."
  }, [isScheduling, schedulableTakeout, takeoutProvisioned, storeToggledOff, takeoutOpen, takeoutService])

  const deliveryClosedHint = useMemo(() => {
    if (isScheduling) {
      return schedulableDelivery ? null : "Delivery não aceita agendamento no momento."
    }
    if (!deliveryProvisioned) return "Delivery não disponível neste estabelecimento."
    if (storeToggledOff) return "A loja está fechada no momento."
    if (deliveryOpen) return null
    const next = deliveryService?.serviceHours?.weekHours
      ? getNextOpeningMessage(deliveryService.serviceHours.weekHours)
      : null
    return next ? `Delivery fechado. ${next}` : "Delivery indisponível no momento."
  }, [isScheduling, schedulableDelivery, deliveryProvisioned, storeToggledOff, deliveryOpen, deliveryService])

  const quotes = useDeliveryQuotes(
    getStoredCompany()?.id,
    addresses,
    isScheduling,
    deliveryService != null,
  )
  const evaluated = applyDeliveryQuotes(
    addresses,
    evaluateAddresses(addresses, deliveryService?.serviceArea, basicInfo?.address, deliveryAreaConfig),
    quotes,
  )
  const quotesPending = evaluated.some((a) => a.pending)
  const selectedQuote =
    state.serviceType === "DELIVERY" ? evaluated.find((a) => a.address.id === state.addressId) : undefined
  const quotedFee = selectedQuote && !selectedQuote.pending && selectedQuote.deliverable ? selectedQuote.price : null

  useEffect(() => {
    if (quotedFee == null) return
    setState((prev) => (prev.servicePrice === quotedFee ? prev : { ...prev, servicePrice: quotedFee }))
  }, [quotedFee, setState])
  const hasDeliverableAddress = quotesPending || evaluated.some((a) => a.deliverable)

  function selectTakeout() {
    if (!takeoutOpen) return
    setState((prev) => ({
      ...prev,
      serviceType: "TAKEOUT",
      addressId: undefined,
      servicePrice: 0,
      scheduledSlot: prev.serviceType === "TAKEOUT" ? prev.scheduledSlot : null,
      scheduledArrival: prev.serviceType === "TAKEOUT" ? prev.scheduledArrival : null,
      scheduledDate: prev.serviceType === "TAKEOUT" ? prev.scheduledDate : null,
      scheduledPayment: takeoutChannel?.payment ?? null,
    }))
  }

  function selectDelivery(addressId: string, price: number) {
    if (!deliveryOpen) return
    setState((prev) => ({
      ...prev,
      serviceType: "DELIVERY",
      addressId,
      servicePrice: price,
      scheduledSlot: prev.serviceType === "DELIVERY" && prev.addressId === addressId
        ? prev.scheduledSlot
        : null,
      scheduledArrival: prev.serviceType === "DELIVERY" && prev.addressId === addressId
        ? prev.scheduledArrival
        : null,
      scheduledDate: prev.serviceType === "DELIVERY" && prev.addressId === addressId
        ? prev.scheduledDate
        : null,
      scheduledPayment: deliveryChannel?.payment ?? null,
    }))
  }

  const storeLines = formatAddressLines(basicInfo?.address ?? {})

  const validation = stepGuards.service({
    state,
    cartTotal: cart.totalPrice,
    hasDeliverableAddress: (addressId) => {
      const found = evaluated.find((a) => a.address.id === addressId)
      return Boolean(found?.deliverable)
    },
  })

  const scheduleStepVisible = isScheduleStepVisible(
    state,
    scheduling.schedulableChannels.map((channel) => channel.serviceType),
  )

  return (
    <>
      <StepHeader
        title="Como você quer receber seu pedido?"
        description={
          isScheduling
            ? "Escolha o canal. O horário vem no próximo passo, e cada canal tem sua própria janela de agendamento."
            : "O cardápio é o mesmo; cada forma de receber tem seu próprio horário de funcionamento."
        }
      />

      <PageContainer as="section" className="space-y-6">
        {takeoutProvisioned ? (
        <div>
          <SectionHeader title="Retirar na loja" />
          <SelectableCard
            selected={state.serviceType === "TAKEOUT"}
            onSelect={selectTakeout}
            indicator="radio"
            disabled={!takeoutOpen}
            icon={<Icon as={Pin} size="xl" />}
            title={storeLines.line1}
            subtitle={
              <>
                {storeLines.line2}
                {takeoutClosedHint ? (
                  <Text as="span" weight="semibold" tone="danger" className="mt-1 block">
                    {takeoutClosedHint}
                  </Text>
                ) : basicInfo?.averagePreparationTime != null && basicInfo.averagePreparationTime > 0 ? (
                  <Text as="span" weight="semibold" tone="secondary" className="mt-1 block">
                    Pronto em cerca de {basicInfo.averagePreparationTime} min
                  </Text>
                ) : null}
              </>
            }
            aria-label="Retirar na loja"
            tone="secondary"
            density="sm"
            wrapTitle
          />
        </div>
        ) : null}

        {deliveryProvisioned ? (
        <div>
          <SectionHeader title="Receber no meu endereço" />
          {deliveryClosedHint ? (
            <Text size="base" weight="semibold" tone="danger" className="mb-3">
              {deliveryClosedHint}
            </Text>
          ) : null}
          {evaluated.length > 0 ? (
          <SelectableCardGroup
            label="Endereço de entrega"
            value={state.serviceType === "DELIVERY" ? (state.addressId ?? null) : null}
            onValueChange={(addressId) => {
              const target = evaluated.find((entry) => entry.address.id === addressId)
              if (target?.deliverable) selectDelivery(addressId, target.price)
            }}
          >
            {evaluated.map(({ address, deliverable, price, estimateMinutes, pending, reason }) => {
              const enabled = deliverable && deliveryOpen
              const lines = formatAddressLines(address)
              return (
              <SelectableCard
                key={address.id}
                value={address.id}
                disabled={!enabled}
                icon={<Icon as={Pin} size="xl" />}
                title={lines.line1}
                subtitle={
                  <>
                    {lines.line2}
                    {!deliveryOpen ? (
                      <Text as="span" weight="semibold" tone="danger" className="mt-1 block">
                        Delivery fechado no momento
                      </Text>
                    ) : pending ? (
                      <Text as="span" weight="semibold" tone="muted" className="mt-1 block">
                        Calculando a taxa de entrega…
                      </Text>
                    ) : deliverable ? (
                      <Text as="span" weight="semibold" tone="secondary" className="mt-1 block">
                        Taxa <Price value={price} free />
                        {estimateMinutes > 0 ? ` • Entrega em ~${estimateMinutes} min` : ""}
                      </Text>
                    ) : (
                      <Text as="span" weight="semibold" tone="danger" className="mt-1 block">
                        {undeliverableHint(reason)}
                      </Text>
                    )}
                  </>
                }
                aria-label={`Endereço ${lines.line1}`}
                tone="secondary"
                density="sm"
                wrapTitle
              />
            )})}
          </SelectableCardGroup>
          ) : null}

          {!hasDeliverableAddress ? (
            <Callout
              tone="warning"
              size="lg"
              emphasis="strong"
              title="Não encontramos um endereço dentro da área de entrega."
              role="none"
              className="mt-4"
              action={
                <ButtonLink to="/cart/new-address" variant="outline" size="sm">
                  <Icon as={Plus} size="md" />
                  Adicionar endereço
                </ButtonLink>
              }
            >
              {takeoutOpen
                ? "Você ainda pode retirar na loja, ou cadastrar um novo endereço."
                : "Cadastre um novo endereço dentro da área de entrega."}
            </Callout>
          ) : (
            <ButtonLink to="/cart/new-address" variant="plain" tone="primary" size="auto" weight="semibold" className="mt-4">
              <Icon as={Plus} size="md" />
              Adicionar outro endereço
            </ButtonLink>
          )}
        </div>
        ) : null}

        {!state.serviceType ? (
          <Card className="flex items-center gap-3">
            <Icon as={Motorcycle} size="xl" tone="primary" />
            <Text tone="muted">Toque em uma das opções acima para escolher como você quer receber.</Text>
          </Card>
        ) : null}
      </PageContainer>

      <StepFooter
        showBack={scheduling.timingStepVisible}
        onBack={() => navigate("/cart/checkout/timing", { replace: true })}
        continueLabel="Continuar"
        continueDisabled={!validation.ok}
        continueHint={validation.hint}
        onContinue={() =>
          navigate(scheduleStepVisible ? "/cart/checkout/schedule" : "/cart/checkout/payment")
        }
      />
    </>
  )
}

function undeliverableHint(reason: DeliveryQuoteReason | null): string {
  switch (reason) {
    case "ADDRESS_NOT_FOUND":
      return "Não localizamos esse endereço no mapa. Edite o endereço e confirme o ponto."
    case "DELIVERY_NOT_OFFERED":
      return "Esta loja não está fazendo entregas no momento."
    default:
      return "Endereço fora da área de entrega"
  }
}
